import {Injectable} from '@angular/core';
import {AsyncSubject, BehaviorSubject, defer, firstValueFrom, Observable, throwError} from 'rxjs';
import {fromPromise} from 'rxjs/internal/observable/innerFrom';
import {catchError, debounceTime, finalize, map, retry, switchMap, takeUntil, tap} from 'rxjs/operators';
import {destroy$} from '../../../components/destroyable';
import {TxHistoryRec} from '../tx-history-rec';
import {TxUserBrowserStorageService} from '../tx-user-browser-storage.service';
import {TransactionApiService} from './transaction-api.service';

const enum Action {
    CREATE = 'CREATE',
    UPDATE = 'UPDATE'
}

interface DebouncedUpdateRec {
    subject: BehaviorSubject<TxHistoryRec>;
    done$: Observable<TxHistoryRec>;
}

@Injectable({ providedIn: 'root' })
export class TxApiStorageMiddlewareService {

    private readonly destroy$ = destroy$();

    private readonly retryAttempts = 3;
    private readonly retryDelay = 1000; // 1 second
    private readonly updateRequestDebounceTime = 500;

    private initPromise: Promise<void>;

    private readonly debouncedUpdatesToApi = new Map<string, DebouncedUpdateRec>();

    constructor(
        private transactionApiService: TransactionApiService,
        private txUserBrowserStorageService: TxUserBrowserStorageService
    ) {}

    public async init(): Promise<void> {
        this.initPromise ??= this.processPendingLocalData();

        await this.initPromise;
    }

    public createTransaction(tx: TxHistoryRec): Observable<TxHistoryRec> {
        return this.tryApiOperation(Action.CREATE, tx);
    }

    public asyncCreateTransaction(tx: TxHistoryRec): Promise<TxHistoryRec> {
        return firstValueFrom(this.createTransaction(tx).pipe(
            takeUntil(this.destroy$)
        ));
    }

    public updateTransaction(tx: TxHistoryRec): Observable<TxHistoryRec> {
        return defer(() => {
            let debounced = this.debouncedUpdatesToApi.get(tx.hash);

            if (!debounced) {
                const subject = new BehaviorSubject<TxHistoryRec>(tx);
                const doneSubject = new AsyncSubject<TxHistoryRec>();

                subject.pipe(
                    debounceTime(this.updateRequestDebounceTime),
                    switchMap(tx => this.tryApiOperation(Action.UPDATE, tx)),
                    tap(resultTx => {
                        doneSubject.next(resultTx);
                        doneSubject.complete();
                    }),
                    finalize(() => this.debouncedUpdatesToApi.delete(tx.hash)),
                    takeUntil(this.destroy$)
                ).subscribe();

                debounced = {
                    subject,
                    done$: doneSubject.asObservable()
                };
            }

            return debounced.done$;
        });
    }

    public asyncUpdateTransaction(tx: TxHistoryRec): Promise<TxHistoryRec> {
        return firstValueFrom(this.updateTransaction(tx).pipe(
            takeUntil(this.destroy$)
        ));
    }

    private tryApiOperation(action: Action, tx: TxHistoryRec): Observable<TxHistoryRec> {
        const apiOperation$ = action === Action.CREATE
            ? this.transactionApiService.createTransaction(tx)
            : this.transactionApiService.updateTransaction(tx);

        return apiOperation$.pipe(
            catchError(error => {
                console.warn(`Failed to ${action} transaction via API, storing in localStorage`, error);

                return fromPromise(this.storeLocally(tx)).pipe(
                    map(() => tx),
                    takeUntil(this.destroy$)
                );
            })
        );
    }

    private async storeLocally(tx: TxHistoryRec): Promise<void> {
        await this.txUserBrowserStorageService.setTransaction(tx);
    }

    private async getLocalPendingOperations(): Promise<TxHistoryRec[]> {
        return await this.txUserBrowserStorageService.getAllTransactions();
    }

    private async processPendingLocalData(): Promise<void> {
        const txs = await this.getLocalPendingOperations();

        if (txs.length === 0) {
            return;
        }

        console.log(`Found ${txs.length} locally stored transactions, sending to the API...`);

        for (const tx of txs) {
            try {
                await this.retryCreateTransaction(tx);

                // If successful, remove from pending operations
                await this.removePendingOperation(tx);
            } catch (error) {
                console.error(`Failed to send the transaction to API: ${tx.hash}`, error);
                // Keep the operation in storage for later retry
            }
        }
    }

    private async retryCreateTransaction(tx: TxHistoryRec): Promise<void> {
        await firstValueFrom(
            this.transactionApiService.createTransaction(tx).pipe(
                retry({
                    delay: this.retryDelay,
                    count: this.retryAttempts
                }),
                catchError(error => {
                    console.error(`Failed to create transaction after retries: ${tx.hash}`, error);
                    return throwError(() => error);
                })
            )
        );
    }

    // TODO: Remove?
    private async retryUpdateTransaction(tx: TxHistoryRec): Promise<void> {
        await firstValueFrom(
            this.transactionApiService.updateTransaction(tx).pipe(
                retry({
                    delay: this.retryDelay,
                    count: this.retryAttempts
                }),
                catchError(error => {
                    console.error(`Failed to update transaction after retries: ${tx.hash}`, error);
                    return throwError(() => error);
                })
            )
        );
    }

    private async removePendingOperation(tx: TxHistoryRec): Promise<void> {
        await this.txUserBrowserStorageService.deleteTransaction(tx.hash);
    }

}
